// Theme Colors
// Organized by rainbow spectrum with MUI button variant colors

export const colors = {
    // RED PALETTE
    // Used for error states, danger actions, and critical information
    red: {
        primary: "#f44336", // MUI error.main
        light: "#ef5350", // MUI error.light
        dark: "#d32f2f", // MUI error.dark
        contrast: "#ffffff", // MUI error.contrastText
    },

    // PINK PALETTE
    // Used for webinar category and accent elements
    pink: {
        primary: "#e91e63", // Webinar category color
        light: "#f06292", // MUI pink light
        dark: "#c2185b", // MUI pink dark
        contrast: "#ffffff",
    },
    // Alias: rose color requested for articles
    rose: {
        primary: "#e91e63",
        light: "#f06292",
        dark: "#c2185b",
        contrast: "#ffffff",
    },

    // PURPLE PALETTE
    // Used for networking category, borders, and primary brand elements
    purple: {
        primary: "#9c27b0", // Networking category, borders, buttons
        light: "#ba68c8", // MUI purple light
        dark: "#7b1fa2", // Hover states, dark variant
        contrast: "#ffffff",
    },

    // BLUE PALETTE
    // Used for conference category and informational elements
    blue: {
        primary: "#2196f3", // Conference category color
        light: "#64b5f6", // MUI blue light
        dark: "#1976d2", // Hover states, dark variant
        contrast: "#ffffff",
    },

    // ORANGE PALETTE
    // Used for training category and warning states
    orange: {
        primary: "#ff9800", // Training category color
        light: "#ffb74d", // MUI orange light
        dark: "#f57c00", // MUI orange dark
        contrast: "#000000",
    },

    // GREEN PALETTE
    // Used for workshop category, success states, and positive actions
    green: {
        primary: "#4caf50", // Workshop category, success actions
        light: "#81c784", // MUI green light
        dark: "#45a049", // Hover states, darker variant
        contrast: "#ffffff",
    },

    // YELLOW PALETTE
    // Used for warnings and attention badges
    yellow: {
        primary: "#ffcc00",
        light: "#ffe066",
        dark: "#cc9900",
        contrast: "#ffffff",
    },

    // INDIGO/VIOLET PALETTE
    // Used for outlines and accents
    indigo: {
        primary: "#7b61ff",
        light: "#a190ff",
        dark: "#5a3cff",
        contrast: "#ffffff",
    },

    // NEUTRAL/GRAY PALETTE
    // Used for default states, disabled elements, and secondary text
    gray: {
        primary: "#757575", // Default category color, secondary text
        light: "#9e9e9e", // MUI gray light
        dark: "#424242", // MUI gray dark
        contrast: "#ffffff",
    },

    // SEMANTIC COLORS
    // Special purpose colors with transparency and overlays
    overlay: {
        dark: "rgba(0, 0, 0, 0.7)", // Dark overlay for modals/backdrops
        light: "rgba(0, 0, 0, 0.4)", // Light overlay for images
        green: "rgba(76, 175, 80, 0.1)", // Green background tint
        blue: "rgba(33, 150, 243, 0.1)", // Blue background tint
    },

    // INFORMATIONAL SURFACES
    info: {
        surface: "#e3f2ff", // Soft info background
        accent: "#9cc7ff", // Accent border/background for info blocks
    },

    // STATUS & ALERT COLORS
    status: {
        success: "#00aa00", // Strong success indicator
        successAccent: "#00ff00", // Terminal-style success text
        successSurface: "#e6f6e6", // Success background
        successBorder: "#b6e3b6", // Success border highlight
        warningSurface: "#fff8db", // Warning background
        warningBorder: "#f0e1a6", // Warning border highlight
        warningHighlight: "#fff9e0", // Alternate warning tint
        error: "#aa0000", // Strong error indicator
        errorSurface: "#fff0f0", // Error background
        errorTint: "#fdeaea", // Error soft tint
        errorBorder: "#f3b6b6", // Error border highlight
        errorMuted: "#fff5f5", // Error muted background
    },

    // NEUTRAL/UTILITY COLORS
    neutral: {
        border: "#e0e0e0", // Neutral border color
        softBackground: "#fafbff", // Neutral soft background
    },

    // UTILITY COLORS
    utility: {
        terminalBackground: "#111111",
    },

    // BACKGROUND COLORS
    background: {
        primary: "#ffffff",
        secondary: "#f5f5f5",
        paper: "#ffffff",
    },

    // COMMON
    common: {
        black: "#000000",
        white: "#ffffff",
    },
};

// Category-specific color mapping
export const categoryColors = {
    workshop: colors.green.primary,
    conference: colors.blue.primary,
    "trade show": colors.indigo.primary,
    networking: colors.purple.primary,
    training: colors.orange.primary,
    webinar: colors.pink.primary,
    default: colors.gray.primary,
};

// Page-type color mapping (used in requested edits filtering)
export const pageTypeColors: Record<string, string> = {
    organization: colors.orange.primary,
    event: colors.green.primary,
    regulation: colors.blue.primary,
    article: colors.purple.primary,
    admin: colors.rose.primary,
    homepage: "#616161",
};

// HIGH-CONTRAST PALETTE MODIFIERS
// Used by the editor color picker so the light/dark options provide much
// stronger contrast than the standard MUI light/dark variants. Roughly each
// hue's primary mixed 50% with white (lightPalette) or 50% with black
// (darkPalette). Keep these in sync with the picker's HUES order.
export const lightPalette: Record<string, string> = {
    rose: "#fac1d3", // ~20% lighter than previous (#f48fb1)
    red: "#fcc7c2",
    orange: "#ffe0b2",
    yellow: "#ffeeb2",
    green: "#caead0",
    blue: "#bcdffb",
    purple: "#e0bce8",
};

export const darkPalette: Record<string, string> = {
    rose: "#974b65", // dark rose, lightened 25% toward white
    red: "#9b5854",
    orange: "#9f7940",
    yellow: "#9f8c40",
    green: "#5c815e",
    blue: "#4c789a",
    purple: "#7a4e82",
};

// CONTRAST LOOKUP
// Maps every swatch value exposed by the editor's color picker to the text
// color that maintains adequate contrast when used as a background fill.
// Keep in sync with `lightPalette`, `colors[hue].primary`, and `darkPalette`.
const _hueContrastKeys: Array<keyof typeof colors> = [
    "rose",
    "red",
    "orange",
    "yellow",
    "green",
    "blue",
    "purple",
];
export const paletteContrastMap: Record<string, string> = (() => {
    const m: Record<string, string> = {};
    _hueContrastKeys.forEach((h) => {
        // Light shades: always pair with black text
        m[lightPalette[h as string].toLowerCase()] = "#000000";
        // Primary shades: use the explicit contrast color from the palette
        const c = (colors[h] as Record<string, string>).contrast || "#000000";
        m[(colors[h] as Record<string, string>).primary.toLowerCase()] = c;
        // Dark shades: always pair with white text
        m[darkPalette[h as string].toLowerCase()] = "#ffffff";
    });
    // Neutrals
    m["#ffffff"] = "#000000";
    m["#9e9e9e"] = "#ffffff";
    m["#000000"] = "#ffffff";
    return m;
})();

/**
 * Look up a high-contrast text color for the supplied background. Returns
 * `null` if the background isn't part of the editor's known palette so callers
 * can leave the existing text color unchanged.
 */
export function getContrastForBackground(bg: string): string | null {
    if (!bg) return null;
    const key = bg.trim().toLowerCase();
    return paletteContrastMap[key] ?? null;
}

// Export individual color palettes for convenience
export const {
    red,
    pink,
    rose,
    purple,
    blue,
    orange,
    green,
    yellow,
    indigo,
    gray,
    overlay,
    info,
    status,
    neutral,
    background,
    common,
    utility,
} = colors;
